import { BatchEmission, BatchEntry } from './desktop-notification-batch';

// ─────────────────────────────────────────────────────────────────────────────
// Deferred notification queue
// ─────────────────────────────────────────────────────────────────────────────
// Events that had nowhere to go: the surface was an OS banner, and the user has
// banners switched off. Dropping them left the bell badge as the entire feedback
// for a user who was away. They are held here instead and announced as one
// in-app toast when a Volta tab next regains focus.
//
// Pure reducers over a serialisable state, mirroring desktop-notification-batch,
// so the storage service stays a thin localStorage wrapper.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How long an undelivered event stays worth announcing. An order that finished
 * during a coffee break is news; one that finished overnight is not, and the bell
 * is the complete record for anything older.
 *
 * **Product decision, deliberately one constant.** 30 minutes is a starting value,
 * not a researched one.
 */
export const DESKTOP_DEFERRED_TTL_MS = 1_800_000;

/** Hard cap, so a browser left open all weekend cannot grow the queue without bound. */
export const DESKTOP_DEFERRED_MAX = 50;

/**
 * What the caller hands over: the entry to render, plus the id that identifies the
 * underlying notification.
 *
 * The id is carried so the queue can be **idempotent on its own terms**. It would be
 * tempting to lean on the claim ledger for that, but the ledger's `DESKTOP_SEEN_TTL_MS`
 * is 5 minutes — six times shorter than the hold this queue provides. A notification
 * redelivered after the ledger entry expired would be claimed afresh and queued twice,
 * and the user would return to "2 orders are ready" for one order. Owning the check here
 * means the guarantee does not depend on another module's unrelated TTL.
 */
export interface DeferredInput extends BatchEntry {
  notificationId: number;
}

export interface DeferredEntry extends DeferredInput {
  /** When the event was deferred, for expiry. */
  ts: number;
}

export interface DeferredState {
  entries: DeferredEntry[];
}

export const EMPTY_DEFERRED_STATE: DeferredState = { entries: [] };

/** Entries still young enough to be worth announcing. */
function live(entries: DeferredEntry[], now: number): DeferredEntry[] {
  return entries.filter((entry) => now - entry.ts < DESKTOP_DEFERRED_TTL_MS);
}

function distinctEventType(entries: DeferredEntry[]): DeferredEntry['eventType'] | null {
  const first = entries[0].eventType;
  return entries.every((entry) => entry.eventType === first) ? first : null;
}

/**
 * Holds one event for the user's return. Expiry runs on write as well as on read, so a
 * queue nobody ever drains cannot accumulate a week of dead events; the cap then bounds
 * a single burst, keeping the newest because they are the ones still worth telling.
 */
export function defer(state: DeferredState, now: number, input: DeferredInput): DeferredState {
  const announceable = live(state.entries, now);

  // Already owed: keep the original, timestamp and all. Re-stamping would let a
  // redelivered event walk its own expiry forward indefinitely.
  if (announceable.some((entry) => entry.notificationId === input.notificationId)) {
    return { entries: announceable };
  }

  const appended = [...announceable, { ...input, ts: now }];
  return { entries: appended.slice(-DESKTOP_DEFERRED_MAX) };
}

/**
 * Everything owed, as one emission, leaving the queue empty — returning twice must not
 * announce twice. The emission shape is the batch window's, so the caller renders a
 * deferred group through exactly the same copy as a live one.
 */
export function drain(
  state: DeferredState,
  now: number,
): { state: DeferredState; emission: BatchEmission | null } {
  const announceable = live(state.entries, now);

  if (announceable.length === 0) {
    return { state: EMPTY_DEFERRED_STATE, emission: null };
  }
  if (announceable.length === 1) {
    // The renderer takes a plain BatchEntry — `ts` and `notificationId` are this
    // queue's bookkeeping and must not leak into the emission.
    const { ts, notificationId, ...entry } = announceable[0];
    return { state: EMPTY_DEFERRED_STATE, emission: { kind: 'single', entry } };
  }
  return {
    state: EMPTY_DEFERRED_STATE,
    emission: {
      kind: 'grouped',
      count: announceable.length,
      eventType: distinctEventType(announceable),
    },
  };
}
